import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query'

import { knockOutPublicFetcher } from '@/libs/fetcher/presets'

import { pickEventListRefreshInterval } from './eventListRefreshInterval'
import { navFilterToEventParams } from './navFilterToEventParams'
import type {
  PredictionContractGroup,
  PredictionEvent,
  PredictionEventsPage,
  PredictionEventsParams,
} from './types'

const PAGE_LIMIT = 20

/**
 * `game_status=scheduled,inprogress`，仅 sports lane（cdc-web CategoryPage
 * `CATEGORY_PAGE_GAME_STATUS` / og `DEFAULT_GAME_STATUS`）：砍掉已 final 的
 * 比赛。`game_status` 是 match 概念，predicts/crypto 这类无 match status 的
 * 类目附加它是语义污染，所以只对 asset_type 含 sports 的过滤生效。
 */
const SPORTS_GAME_STATUS = 'scheduled,inprogress'

export function predictionEventsInfiniteQueryOptions(
  params: PredictionEventsParams,
  pageLimit = PAGE_LIMIT,
) {
  // Live 节点（filter 含 "live":"true"）：BE 的 live=true 是特殊路由，与
  // asset_type 组合会被静默收窄（asset_type=predicts&live=true 查无数据），
  // 所以不传 asset_type；但 live 树的 event_kind_groups 等组合键保留
  // （navFilterToEventParams 只丢 live 键），live 子分类 = live=true +
  // event_kind_groups=<uuid>。排序仍用 sort_by（live 路由支持）。
  const isLiveNode = params.navNode?.filter?.live === 'true'
  return infiniteQueryOptions({
    queryKey: [
      'prediction',
      'events',
      params.navNode?.id ?? 'all',
      params.sortBy,
      params.navNode?.filter ?? null,
      isLiveNode,
      pageLimit,
    ] as const,
    // 切 L1/L2/sort 时 queryKey 更换，新查询瞬时无 data——用 keepPreviousData
    // 让旧页卡片撑住画面（不闪白/不闪 empty），新数据到达后原位替换
    // （RQ v5 对 infinite query 的 placeholder 会在首次 fetch 后整页替换）。
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }): Promise<PredictionEventsPage> => {
      const filterParams = navFilterToEventParams(params.navNode?.filter ?? null)
      const queries: Record<string, string> = {
        ...(isLiveNode ? { live: 'true' } : { asset_type: 'predicts' }),
        status: 'active',
        limit: String(pageLimit),
        sort_by: params.sortBy,
        ...filterParams,
      }
      // sports lane 才附加 game_status（见 SPORTS_GAME_STATUS 注释）；判断基于
      // 过滤里实际生效的 asset_type，而非默认的 predicts。live 节点同上：
      // 在播事件集中在 sports，game_status 只砍已 final 的比赛。
      if (isLiveNode || filterParams.asset_type?.split(',').includes('sports')) {
        queries.game_status = SPORTS_GAME_STATUS
      }
      if (pageParam) queries.starting_after = pageParam
      // successResponseDataProperty: '' keeps the raw {has_more, data} body —
      // the fetcher's default `.data` unwrap would drop the pagination field
      // (repo pattern, see fetchCoins in services/coin/coins.ts).
      const res = await knockOutPublicFetcher<PredictionEventsPage>(
        '/api/v1/events',
        { queries },
        { successResponseDataProperty: '' },
      )
      return res ?? { has_more: false, data: [] }
    },
    initialPageParam: '',
    getNextPageParam: lastPage =>
      lastPage.has_more && lastPage.data.length > 0
        ? lastPage.data[lastPage.data.length - 1].id
        : undefined,
    // 自适应轮询（与 cdc-web 一致）：进行中赛事 5s、临开赛对齐唤醒、常规 30s、
    // 空列表 0（关闭）。RQ 回调拿 query.state.data（分页结构），flatMap 成平列表。
    refetchInterval: query =>
      pickEventListRefreshInterval(query.state.data?.pages.flatMap(p => p.data)),
  })
}

/**
 * 当前事件查询实际生效的 asset_type（默认 'predicts'，nav filter 可覆盖）——
 * 给 event kind groups 查询用（组列表按 asset_type 拉取）。live 节点的
 * filter 只有哨兵键，在播事件集中在 sports（stag 实测），按 sports 拉组列表。
 */
export function predictionEventsAssetType(filter: Record<string, string> | null): string {
  if (filter?.live === 'true') return 'sports'
  return filter?.asset_type ?? 'predicts'
}

/**
 * 把 `/public/api/v1/contracts` 批量结果 merge 回事件（cdc-web
 * CategoryPage 同款）：league → `event.contracts` 顶层，match →
 * `event.sections`。enrichment 未返回的事件保持原样（卡片按无合约渲染）。
 */
export function enrichEventsWithContracts(
  events: PredictionEvent[],
  groups: PredictionContractGroup[] | undefined,
): PredictionEvent[] {
  if (!groups?.length) return events
  const byEventId = new Map(groups.map(g => [g.event_id, g]))
  return events.map(event => {
    const group = byEventId.get(event.id)
    if (!group) return event
    return { ...event, contracts: group.contracts, sections: group.sections ?? event.sections }
  })
}
